import { HistoryItem } from '../Timeline/types';
import {
  AssemblyBackflowTest,
  BackflowAssemblyVersion,
  NoticeSent,
} from './types';
import { parseISO } from 'date-fns';
import { Box } from 'rebass';
import {
  constantToLabel,
  toString,
} from '@ecosconnect/core/src/helpers/string';
import React from 'react';
import { UserMode } from '../../hooks/useApolloClient';
import colors from '../../theme/colors';
import { IconType } from '../Icon/Icon';
import { Column } from '../../hooks/useTablePages';
import StatusChip from '../Status/StatusChip';

export const convertNoticeSent = ({
  id,
  created,
  notice,
}: NoticeSent): HistoryItem<'NOTICE_SENT'> => ({
  id,
  created,
  profile: notice.profile,
  type: 'NOTICE_SENT',
  title: `Sent ${notice.type}`,
  icon: 'mail',
});

export const convertBackflowTest = (
  {
    jobId,
    job,
    dateTested,
    initialResult,
    finalResult,
    testerName,
    testingCompanyName,
    testingCompanySubmitted,
    waterSupplierStatus,
  }: AssemblyBackflowTest,
  mode: UserMode
): HistoryItem<'BACKFLOW_TEST'> => {
  const commentFormData = job.formDatas.find(
    (value) => value.field === 'comments'
  );
  const comment = commentFormData?.value || '';

  let iconColor: string | undefined = undefined;
  let title = '';
  let icon: IconType | undefined;

  if (waterSupplierStatus === null) {
    title = 'Unsubmitted Test';
    iconColor = colors.statusBlue;
    icon = 'hour-glass';
  } else if (waterSupplierStatus === 'REJECTED') {
    title = 'Rejected Test';
    iconColor = colors.statusError;
    icon = 'hour-glass';
  } else if (initialResult === 'FAIL' && finalResult === 'PASS') {
    iconColor = colors.failPass;
    title = 'Failed then Passed';
  } else if (finalResult === 'PASS') {
    iconColor = colors.statusSuccess;
    title = 'Passed Test';
    icon = 'check';
  } else if (finalResult === 'FAIL') {
    iconColor = colors.statusError;
    title = finalResult === null ? 'Not Tested' : 'Failed Test';
    icon = 'x';
  } else {
    title = 'Backflow Test';
  }

  return {
    id: jobId,
    created: dateTested
      ? parseISO(dateTested).getTime()
      : (testingCompanySubmitted ?? job.created),
    profile: {
      name: testerName
        ? `${testerName}@${testingCompanyName}`
        : testingCompanyName,
    },
    type: 'BACKFLOW_TEST',
    icon,
    iconColor,
    title,
    url: `/${mode}/test-reports/${jobId}`,
    comment,
  };
};

type AssemblyFieldChange = {
  field: string;
  from: string | number | null | undefined;
  to: string | number | null | undefined;
};

type AssemblyCheckedField =
  | 'serialNumber'
  | 'size'
  | 'type'
  | 'model'
  | 'location';

export const diffAssembly = (
  from: BackflowAssemblyVersion,
  to: BackflowAssemblyVersion
): AssemblyFieldChange[] => {
  const fields: AssemblyCheckedField[] = [
    'type',
    'model',
    'serialNumber',
    'size',
    'location',
  ];
  return fields.reduce((changes, field) => {
    if (field === 'model') {
      if (from.model?.name !== to.model?.name) {
        changes.push({
          field,
          from: from.model?.name,
          to: to.model?.name,
        });
      }
    } else if (from[field] !== to[field]) {
      changes.push({
        field: field === 'serialNumber' ? 'serial' : field,
        from: toString(from[field]).replace(/_/g, ' '),
        to: toString(to[field]).replace(/_/g, ' '),
      });
    }
    return changes;
  }, [] as AssemblyFieldChange[]);
};

const changeSetColumns: Column[] = [
  {
    header: 'Field',
    accessor: 'field',
    width: 100,
  },
  {
    header: 'From',
    accessor: 'from',
    width: 150,
  },
  {
    header: 'To',
    accessor: 'to',
    width: 150,
  },
];

export const convertAssemblyVersion = (
  version: BackflowAssemblyVersion,
  previousVersion: BackflowAssemblyVersion | undefined,
  mode: UserMode
): HistoryItem<'VERSION'> => {
  const changes = previousVersion
    ? diffAssembly(previousVersion, version)
    : undefined;

  return {
    id: version.id,
    created: version.created,
    profile: version.session?.userProfile ?? { name: 'Unknown ' },
    type: 'VERSION',
    icon: changes ? 'edit' : undefined,
    title: changes ? 'Modified' : 'Created',
    url: previousVersion
      ? `/${mode}/assemblies/${previousVersion.id}`
      : undefined,
    comment: changes ? (
      // <DataTable columns={changeSetColumns} items={changes} />
      <>
        {changes.map(({ field, from, to }) => (
          <Box fontSize="12px" key={field} lineHeight="24px">
            {changes.length > 1 ? <>&bull; </> : ''}
            {constantToLabel(field)}{' '}
            <StatusChip fontSize="inherit">{toString(from)}</StatusChip> →{' '}
            <StatusChip fontSize="inherit">{toString(to)}</StatusChip>
          </Box>
        ))}
      </>
    ) : undefined,
  };
};
